import { useEffect, useRef, useState } from 'react'
import type { PriceUpdate } from '../services/signalrService'

export type { PriceUpdate }

interface UseSignalROptions {
  onPriceUpdate?: (update: PriceUpdate) => void
  enabled?: boolean
}

export function useSignalR(options: UseSignalROptions = {}) {
  const { onPriceUpdate, enabled = true } = options
  const [isConnected, setIsConnected] = useState(false)
  const [isLive, setIsLive] = useState(false)
  const lastUpdateRef = useRef(0)
  const callbackRef = useRef(onPriceUpdate)
  callbackRef.current = onPriceUpdate

  // Batch buffer for RAF-based batching
  const batchRef = useRef<PriceUpdate[]>([])
  const rafRef = useRef<number>(0)

  // Lazy-load signalrService & connect after 3s delay (don't block LCP)
  useEffect(() => {
    if (!enabled) return

    let cancelled = false
    let unsubscribe: (() => void) | undefined
    let cleanupVisibility: (() => void) | undefined
    let pollInterval: ReturnType<typeof setInterval> | undefined

    const timer = setTimeout(async () => {
      try {
        const { signalrService } = await import('../services/signalrService')
        if (cancelled) return

        await signalrService.connect()
        if (cancelled) return
        setIsConnected(signalrService.isConnected())

        // Subscribe to price updates with RAF batching
        unsubscribe = signalrService.onPriceUpdate((update) => {
          lastUpdateRef.current = Date.now()
          setIsLive(true)

          batchRef.current.push(update)
          if (!rafRef.current) {
            rafRef.current = requestAnimationFrame(() => {
              const batch = batchRef.current
              batchRef.current = []
              rafRef.current = 0
              for (const u of batch) {
                callbackRef.current?.(u)
              }
            })
          }
        })

        // Visibility change: disconnect on hidden, reconnect on visible
        cleanupVisibility = signalrService.setupVisibilityHandler()

        // Poll connection state
        pollInterval = setInterval(() => {
          setIsConnected(signalrService.isConnected())
        }, 5000)
      } catch (err) {
        console.warn('[SignalR] Setup failed:', err)
      }
    }, 3000)

    return () => {
      cancelled = true
      clearTimeout(timer)
      unsubscribe?.()
      cleanupVisibility?.()
      if (pollInterval) clearInterval(pollInterval)
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = 0
      }
    }
  }, [enabled])

  // isLive timeout (30s)
  useEffect(() => {
    if (!isLive) return

    const interval = setInterval(() => {
      if (Date.now() - lastUpdateRef.current > 30_000) {
        setIsLive(false)
      }
    }, 5000)

    return () => clearInterval(interval)
  }, [isLive])

  return { isConnected, isLive }
}
